// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo } from 'react';

import { type CustomProperty, type GetPaginatedOpts, useEvolvePaginatedGet, type WorkOrderId } from '@evolvemep/api-ts';
import { Text, Stack } from '@mantine/core';

import { TriggerOnVisible } from 'hooks/useOnScreen';
import { MachineCutList } from 'modules/Shop/EvolveFabrication/CutLists/MachineCutList';

type Props = {
  workOrderId: WorkOrderId;
  canMarkItemsAsDone: boolean;
  /** The Fabrication Item custom properties to show as editable columns. */
  customProperties: CustomProperty[];
};

export const CutLists = ({ workOrderId, canMarkItemsAsDone, customProperties }: Props) => {
  const defaultOpts = useMemo<GetPaginatedOpts<'shop/fabrication/cutList'>>(
    () => ({
      defaultConfig: {
        params: {
          workOrderId,
        },
      },
    }),
    [workOrderId],
  );
  const {
    data: cutLists,
    fetchNextPage,
    loading,
    setDefaultOpts,
    entireCount,
    reset,
  } = useEvolvePaginatedGet('shop/fabrication/cutList', defaultOpts);
  useEffect(() => {
    setDefaultOpts(defaultOpts);
  }, [defaultOpts, setDefaultOpts]);

  return (
    <Stack style={{ overflowY: 'scroll' }} pr="xs" flex={1}>
      <Stack>
        {cutLists.map((c) => (
          <MachineCutList
            key={c.cutListId}
            cutList={c}
            canMarkItemsAsDone={canMarkItemsAsDone}
            customProperties={customProperties}
            onCutListEmpty={reset}
          />
        ))}
        {entireCount === 0 && <Text c="dimmed">No cut lists available.</Text>}
        <TriggerOnVisible onVisible={fetchNextPage} loading={loading} />
      </Stack>
    </Stack>
  );
};
